import React, { useEffect, useMemo, useState } from 'react';
import { message, Modal } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Users, Search, Loader2, UserCheck, UserX, Building2, LogIn, ShieldOff, Unlock, ChevronDown, ChevronRight } from 'lucide-react';
import { useAppSelector } from '../../app/hooks';
import { selectHasPermission } from '../../features/auth/authSlice';
import { normalizeRoles } from '../../rbac/rbac.generated';
import { listAllUsers, setUserActive, impersonateUser, resetUserMfa, unlockUser, type PlatformUser } from '../../services/platformUsersService';

const MAIN_APP_URL = (import.meta.env.VITE_MAIN_APP_URL as string) || 'http://localhost:3000';

const name = (u: PlatformUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;
const initials = (u: PlatformUser) =>
  name(u).split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const roleLabel = (u: PlatformUser) => normalizeRoles(u.roles || [])[0] || 'employee';

export const UsersManagementPage: React.FC = () => {
  const canDeactivate = useAppSelector(selectHasPermission('user:deactivate'));
  const canImpersonate = useAppSelector(selectHasPermission('user:impersonate'));
  const canResetMfa = useAppSelector(selectHasPermission('user:mfa:reset'));
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listAllUsers({ page: 1, limit: 200 });
      setUsers(Array.isArray(res?.users) ? res.users : []);
    } catch {
      message.error('Could not load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [name(u), u.email, u.tenant?.name].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [users, search]);

  // Tenant → users. Largest tenants first (the scaling view: who has how many users),
  // ties alphabetical; users inside a group alphabetical.
  const groups = useMemo(() => {
    const byTenant = new Map<string, { name: string; users: PlatformUser[] }>();
    for (const u of filtered) {
      const key = u.tenantId || u.tenant?.name || '—';
      const g = byTenant.get(key) ?? { name: u.tenant?.name || 'No tenant', users: [] };
      g.users.push(u);
      byTenant.set(key, g);
    }
    return [...byTenant.entries()]
      .map(([key, g]) => ({ key, ...g, users: g.users.sort((a, b) => name(a).localeCompare(name(b))) }))
      .sort((a, b) => b.users.length - a.users.length || a.name.localeCompare(b.name));
  }, [filtered]);

  // Collapsed by default (the overview: tenant + headcount); searching expands every
  // matching group so results are never hidden behind a closed header.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const searching = search.trim() !== '';
  const isOpen = (key: string) => searching || expanded.has(key);
  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const impersonate = async (u: PlatformUser) => {
    setBusyId(u.id);
    try {
      const res = await impersonateUser(u.id);
      // Hand the minted session to the KaziPeople app (different origin) via the
      // URL hash, opened in a new tab. H-SEC-5: impersonation is a short-lived,
      // NON-renewable session — there is no refresh token to pass.
      const url = `${MAIN_APP_URL}/impersonate#at=${encodeURIComponent(res.accessToken)}`;
      window.open(url, '_blank', 'noopener');
      message.success(`Impersonating ${name(u)} — opened in a new tab`);
    } catch {
      message.error('Could not start impersonation');
    } finally {
      setBusyId(null);
    }
  };

  const resetMfa = (u: PlatformUser) => {
    Modal.confirm({
      title: 'Reset two-factor authentication?',
      content: `${name(u)} will be able to sign in without a code and can set it up again.`,
      okText: 'Reset 2FA',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusyId(u.id);
        try {
          await resetUserMfa(u.id);
          message.success(`2FA reset for ${name(u)}`);
        } catch (e: any) {
          message.error(e?.response?.data?.message || 'Could not reset 2FA');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const toggleActive = async (u: PlatformUser) => {
    const next = !(u.isActive ?? true);
    setBusyId(u.id);
    try {
      await setUserActive(u.id, next);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, isActive: next } : x)));
      message.success(`${name(u)} ${next ? 'activated' : 'deactivated'}`);
    } catch {
      message.error('Could not update user');
    } finally {
      setBusyId(null);
    }
  };

  const unlock = async (u: PlatformUser) => {
    setBusyId(u.id);
    try {
      await unlockUser(u.id);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, lockedUntil: null } : x)));
      message.success(`Unlocked ${name(u)} — they can sign in now`);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not unlock the account');
    } finally {
      setBusyId(null);
    }
  };

  const isLocked = (u: PlatformUser) =>
    !!(u.lockedUntil && new Date(u.lockedUntil).getTime() > Date.now());

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-foreground">Users</h1>
        <p className="text-muted-foreground mt-1">Every user on the platform, grouped by tenant. Open a tenant to manage its users.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2 flex-1">
            <Users className="w-5 h-5" /> {filtered.length} users · {groups.length} tenants
          </CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, tenant…" className="pl-9" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No users found.</div>
          ) : (
            <div className="space-y-2">
              {groups.map((g) => (
                <div key={g.key} className="rounded-xl border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.key)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
                  >
                    {isOpen(g.key) ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <Building2 className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-foreground flex-1">{g.name}</span>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">
                      {g.users.length} {g.users.length === 1 ? 'user' : 'users'}
                    </span>
                  </button>
                  {isOpen(g.key) && (
                    <div className="divide-y divide-border border-t border-border px-4">
                      {g.users.map((u) => {
                        const active = u.isActive ?? true;
                        return (
                  <div key={u.id} className="py-3 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 font-semibold text-sm">
                      {initials(u)}
                    </span>
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-medium text-foreground flex items-center gap-2">
                        {name(u)}
                        {!active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/15 text-red-700">Inactive</span>}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">{u.email}</div>
                    </div>
                    <Badge variant="outline">{roleLabel(u)}</Badge>
                    {canImpersonate && (
                      <button
                        type="button"
                        onClick={() => impersonate(u)}
                        disabled={busyId === u.id}
                        title="Impersonate"
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border border-border text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400 hover:border-orange-200 dark:hover:border-orange-500/40 transition-colors disabled:opacity-60"
                      >
                        <LogIn className="w-3.5 h-3.5" /> Impersonate
                      </button>
                    )}
                    {canDeactivate && (
                      <button
                        type="button"
                        onClick={() => toggleActive(u)}
                        disabled={busyId === u.id}
                        title={active ? 'Deactivate' : 'Activate'}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border transition-colors disabled:opacity-60 ${
                          active ? 'border-border text-muted-foreground hover:text-red-600 dark:text-red-400 hover:border-red-200' : 'border-green-200 text-green-700 dark:text-green-300 hover:bg-green-50'
                        }`}
                      >
                        {busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                        {active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                    {canDeactivate && isLocked(u) && (
                      <button
                        type="button"
                        onClick={() => unlock(u)}
                        disabled={busyId === u.id}
                        title="Unlock account"
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border border-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors disabled:opacity-60"
                      >
                        <Unlock className="w-3.5 h-3.5" /> Unlock
                      </button>
                    )}
                    {canResetMfa && (
                      <button
                        type="button"
                        onClick={() => resetMfa(u)}
                        disabled={busyId === u.id}
                        title="Reset 2FA"
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-[10px] border border-border text-muted-foreground hover:text-orange-600 dark:hover:text-orange-400 hover:border-orange-200 dark:hover:border-orange-500/40 transition-colors disabled:opacity-60"
                      >
                        <ShieldOff className="w-3.5 h-3.5" /> Reset 2FA
                      </button>
                    )}
                  </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersManagementPage;
